import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, Switch, message, Space, Popconfirm, Card, Tabs } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { supabase } from '@/shared/lib/supabase';
import type { ColumnsType } from 'antd/es/table';
import { FormBuilder, FormField } from '../components/FormBuilder';

const { TextArea } = Input;

interface FormData {
  id: string;
  name: string;
  description: string | null;
  fields: FormField[];
  settings: {
    submitButtonText?: string;
    successMessage?: string;
  };
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export default function FormsPage() {
  const navigate = useNavigate();
  const [forms, setForms] = useState<FormData[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingForm, setEditingForm] = useState<FormData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [formFields, setFormFields] = useState<FormField[]>([]);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('forms')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setForms(data || []);
    } catch (error) {
      console.error('Error fetching forms:', error);
      message.error('Erro ao carregar formulários');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingForm(null);
    form.resetFields();
    form.setFieldsValue({ 
      is_active: true,
      submitButtonText: 'Enviar',
      successMessage: 'Formulário enviado com sucesso!'
    });
    setFormFields([]);
    setModalVisible(true);
  };

  const handleEdit = (record: FormData) => {
    setEditingForm(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      is_active: record.is_active,
      submitButtonText: record.settings?.submitButtonText || 'Enviar',
      successMessage: record.settings?.successMessage || 'Formulário enviado com sucesso!',
    });
    setFormFields(record.fields || []);
    setModalVisible(true);
  };

  // Nova regra: impedir exclusão se houver vínculo com workflow ativo
  const handleDelete = async (id: string) => {
    try {
      // 1. Verifica se existe vínculo em client_products com workflow ativo
      const { data: activeWorkflows, error: cwError } = await supabase
        .from('client_products')
        .select('id, form_workflow_id, form_workflow_active')
        .eq('form_id', id)
        .eq('form_workflow_active', true);

      if (cwError) {
        message.error('Não foi possível verificar vínculos com workflows. Por favor, tente novamente ou contate o suporte.');
        return;
      }

      if (activeWorkflows && activeWorkflows.length > 0) {
        const workflowCount = activeWorkflows.length;
        message.error(
          `Este formulário está associado a ${workflowCount} workflow(s) ativo(s) e não pode ser excluído.\n` +
          'Para excluir, desative primeiro o(s) workflow(s) ou remova o vínculo com o(s) mesmo(s).'
        );
        return;
      }

      // 2. Verifica se existe vínculo em products (formulário padrão de um produto)
      const { data: linkedProducts, error: lpError } = await supabase
        .from('products')
        .select('id, name')
        .eq('form_id', id);

      if (lpError) {
        message.error('Não foi possível verificar vínculos com produtos. Por favor, tente novamente ou contate o suporte.');
        return;
      }

      if (linkedProducts && linkedProducts.length > 0) {
        const productNames = linkedProducts.map((p: any) => p.name).join(', ');
        message.error(
          `Este formulário está vinculado aos produto(s): ${productNames}\n` +
          'Para excluir, remova primeiro o vínculo com o(s) produto(s) correspondente(s).'
        );
        return;
      }

      // 3. Se não houver vínculo, pode excluir normalmente
      const { error } = await supabase
        .from('forms')
        .delete()
        .eq('id', id);

      if (error) throw error;
      message.success('Formulário excluído com sucesso');
      fetchForms();
    } catch (error) {
      console.error('Error deleting form:', error);
      message.error('Erro ao excluir formulário. Tente novamente ou contate o suporte.');
    }
  };

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);
    try {
      if (formFields.length === 0) {
        message.error('Adicione pelo menos um campo ao formulário');
        return;
      }

      const formData = {
        name: values.name,
        description: values.description,
        fields: formFields,
        settings: {
          submitButtonText: values.submitButtonText || 'Enviar',
          successMessage: values.successMessage || 'Formulário enviado com sucesso!',
        },
        is_active: values.is_active ?? true,
        updated_at: new Date().toISOString(),
      };

      if (editingForm) {
        const { error } = await supabase
          .from('forms')
          .update(formData)
          .eq('id', editingForm.id);

        if (error) throw error;
        message.success('Formulário atualizado com sucesso');
      } else {
        const { error } = await supabase
          .from('forms')
          .insert(formData);

        if (error) throw error;
        message.success('Formulário criado com sucesso');
      }

      setModalVisible(false);
      form.resetFields();
      setFormFields([]);
      fetchForms();
    } catch (error) {
      console.error('Error saving form:', error);
      message.error('Erro ao salvar formulário');
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns: ColumnsType<FormData> = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Descrição',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Campos',
      dataIndex: 'fields',
      key: 'fields',
      width: 100,
      render: (fields: FormField[]) => `${fields?.length || 0} campos`,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive: boolean) => (
        <span style={{ color: isActive ? '#52c41a' : '#ff4d4f' }}>
          {isActive ? 'Ativo' : 'Inativo'}
        </span>
      ),
      filters: [
        { text: 'Ativo', value: true },
        { text: 'Inativo', value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 'auto',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/admin/form-submissions/${record.id}`)}
          >
            Respostas
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="Tem certeza que deseja excluir este formulário?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Excluir
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title="Gerenciar Formulários"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Novo Formulário
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={forms}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} formulários`,
          }}
        />
      </Card>

      <Modal
        title={editingForm ? 'Editar Formulário' : 'Novo Formulário'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setFormFields([]);
        }}
        onOk={() => form.submit()}
        okText={isSubmitting ? 'Salvando...' : 'Salvar'}
        cancelText="Cancelar"
        width={900}
        confirmLoading={isSubmitting}
        closable={!isSubmitting}
        maskClosable={!isSubmitting}
        keyboard={!isSubmitting}
        cancelButtonProps={{ disabled: isSubmitting }}
      >
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="Informações Básicas" key="1">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              disabled={isSubmitting}
            >
              <Form.Item
                label="Nome"
                name="name"
                rules={[{ required: true, message: 'Nome é obrigatório' }]}
              >
                <Input placeholder="Ex: Formulário de Cadastro" />
              </Form.Item>

              <Form.Item
                label="Descrição"
                name="description"
              >
                <TextArea
                  rows={3}
                  placeholder="Descrição opcional do formulário"
                />
              </Form.Item>

              <Form.Item
                label="Texto do Botão de Envio"
                name="submitButtonText"
              >
                <Input placeholder="Enviar" />
              </Form.Item>

              <Form.Item
                label="Mensagem de Sucesso"
                name="successMessage"
              >
                <TextArea rows={2} placeholder="Formulário enviado com sucesso!" />
              </Form.Item>

              <Form.Item
                label="Ativo"
                name="is_active"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Form>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Campos do Formulário" key="2">
            <FormBuilder fields={formFields} onChange={setFormFields} />
          </Tabs.TabPane>
        </Tabs>
      </Modal>
    </div>
  );
}
